import { useState } from 'react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

/**
 * Accordion Item component for FAQ sections
 */
const AccordionItem = ({
  title,
  children,
  isOpen = false,
  onToggle,
  className = '',
}) => {
  return (
    <div
      className={clsx(
        'border-b border-neutral-200 last:border-b-0',
        className
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={clsx(
          'w-full flex items-center justify-between py-5 px-1',
          'text-left text-neutral-900 font-medium',
          'hover:text-primary-600 transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
        )}
      >
        <span className="pr-4">{title}</span>
        <ChevronDown
          className={clsx(
            'w-5 h-5 flex-shrink-0 text-neutral-400 transition-transform duration-300',
            isOpen && 'transform rotate-180 text-primary-600'
          )}
          aria-hidden="true"
        />
      </button>
      
      <div
        className={clsx(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
        )}
        aria-hidden={!isOpen}
      >
        <div className="text-neutral-600 leading-relaxed px-1">
          {children}
        </div>
      </div>
    </div>
  );
};

AccordionItem.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  className: PropTypes.string,
};

/**
 * Accordion container component
 */
const Accordion = ({
  items = [],
  allowMultiple = false,
  className = '',
}) => {
  const [openItems, setOpenItems] = useState(new Set());

  const handleToggle = (index) => {
    setOpenItems((prev) => {
      const newSet = new Set(allowMultiple ? prev : []);
      if (prev.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-200',
        className
      )}
    >
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          title={item.title}
          isOpen={openItems.has(index)}
          onToggle={() => handleToggle(index)}
          className="px-6"
        >
          {item.content}
        </AccordionItem>
      ))}
    </div>
  );
};

Accordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  allowMultiple: PropTypes.bool,
  className: PropTypes.string,
};

export { Accordion, AccordionItem };
export default Accordion;
